/**
 * Mobile Walkthrough — room-first, sequential capture flow.
 *
 * Phases:
 *   1. room      : user picks/types a room
 *   2. capture   : user picks voice or photo input
 *   3. voice     : live narration → Whisper → parse
 *   4. photo     : image → GPT-5.2 vision → extract
 *   5. confirm   : quick-review draft → save → loop
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { api, categoryLabel, CATEGORIES } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import {
  Microphone,
  Camera,
  CaretLeft,
  ArrowClockwise,
  Check,
  Stop,
  UploadSimple,
  MapPin,
} from "@phosphor-icons/react";

export default function Walkthrough() {
  const { user } = useAuth();
  const [phase, setPhase] = useState("room"); // room | capture | voice | photo | confirm
  const [room, setRoom] = useState("");
  const [recentRooms, setRecentRooms] = useState([]);
  const [draft, setDraft] = useState(null);
  const [processing, setProcessing] = useState(false);
  const [roomCount, setRoomCount] = useState(0);
  const [totalCount, setTotalCount] = useState(0);

  useEffect(() => {
    api.get("/assets").then((r) => {
      const seen = new Set();
      const rooms = [];
      // Seed from user's onboarding-defined rooms first
      for (const r0 of (user?.rooms || [])) {
        if (!seen.has(r0.toLowerCase())) {
          seen.add(r0.toLowerCase());
          rooms.push(r0);
        }
      }
      // Then merge in rooms inferred from existing assets
      for (const a of r.data) {
        if (a.location && !seen.has(a.location.toLowerCase())) {
          seen.add(a.location.toLowerCase());
          rooms.push(a.location);
        }
      }
      setRecentRooms(rooms.slice(0, 12));
    }).catch(() => {});
  }, [user]);

  const startCapture = () => {
    if (!room.trim()) {
      toast.error("Pick a room first.");
      return;
    }
    setPhase("capture");
    setRoomCount(0);
  };

  const resetDraft = () => { setDraft(null); setPhase("capture"); };

  const saveDraft = async (edits = {}) => {
    if (!draft) return;
    const payload = {
      name: draft.name || "Untitled",
      category: draft.category || "other",
      brand: draft.brand || null,
      model: draft.model || null,
      serial: draft.serial || null,
      location: room.trim(),
      condition: draft.condition || null,
      fmv: draft.fmv ?? null,
      rcv: draft.rcv ?? null,
      notes: draft.notes || null,
      photo_paths: draft.photo_paths || [],
      document_paths: [],
      ...edits,
    };
    try {
      await api.post("/assets", payload);
      toast.success(`Recorded · ${payload.name}`);
      setRoomCount((c) => c + 1);
      setTotalCount((c) => c + 1);
      setDraft(null);
      setPhase("capture");
    } catch {
      toast.error("Save failed.");
    }
  };

  const changeRoom = () => {
    setPhase("room");
    setRoomCount(0);
  };

  return (
    <div data-testid="walkthrough-page" className="space-y-6 max-w-xl mx-auto">
      <div className="flex items-center justify-between">
        <Link to="/assets/new" className="inline-flex items-center gap-2 text-sm text-[#5C5F58] hover:text-[#0A192F]" data-testid="walkthrough-back">
          <CaretLeft size={14} /> Exit
        </Link>
        <div className="font-mono-ledger text-[10px] uppercase tracking-[0.25em] text-[#5C5F58]">
          Walkthrough · {totalCount} recorded
        </div>
      </div>

      {/* PHASE 1: Room */}
      {phase === "room" && (
        <section data-testid="phase-room" className="space-y-6">
          <header>
            <div className="font-mono-ledger text-[10px] uppercase tracking-[0.25em] text-[#5C5F58] mb-2">
              Step 1 of 2 · Location
            </div>
            <h1 className="font-serif-display text-4xl text-[#0A192F] leading-tight">Which room are you standing in?</h1>
            <p className="text-sm text-[#5C5F58] mt-3">
              Every asset you record in this session will be tagged to this location. Change rooms anytime.
            </p>
          </header>

          <div className="bg-white border border-[#E0DDD6] p-6 space-y-4">
            <label className="block">
              <span className="block font-mono-ledger text-[10px] uppercase tracking-[0.25em] text-[#5C5F58] mb-2">
                Room / Location
              </span>
              <div className="relative">
                <MapPin size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#5C5F58]" />
                <input
                  data-testid="room-input"
                  autoFocus
                  value={room}
                  onChange={(e) => setRoom(e.target.value)}
                  placeholder="e.g., Primary bedroom, Garage shelf 3"
                  onKeyDown={(e) => { if (e.key === "Enter") startCapture(); }}
                  className="w-full bg-white border border-[#E0DDD6] pl-9 pr-3 py-3 text-base focus:outline-none focus:border-[#1A3622]"
                />
              </div>
            </label>

            {recentRooms.length > 0 && (
              <div>
                <div className="font-mono-ledger text-[10px] uppercase tracking-[0.25em] text-[#5C5F58] mb-2">
                  Recent rooms
                </div>
                <div className="flex flex-wrap gap-1.5" data-testid="recent-rooms">
                  {recentRooms.map((r) => (
                    <button
                      key={r}
                      onClick={() => setRoom(r)}
                      data-testid={`recent-${r}`}
                      className="px-3 py-1.5 text-xs border border-[#E0DDD6] bg-white hover:border-[#1A3622] text-[#0A192F]"
                    >
                      {r}
                    </button>
                  ))}
                </div>
              </div>
            )}

            <button
              onClick={startCapture}
              data-testid="enter-room"
              disabled={!room.trim()}
              className="w-full bg-[#1A3622] text-[#F9F8F6] py-3 text-sm font-medium border border-[#0F2818] hover:bg-[#0F2818] disabled:opacity-50"
            >
              Enter room →
            </button>
          </div>
        </section>
      )}

      {/* PHASE 2: Capture mode picker */}
      {phase === "capture" && (
        <section data-testid="phase-capture" className="space-y-6">
          <header>
            <div className="font-mono-ledger text-[10px] uppercase tracking-[0.25em] text-[#5C5F58] mb-2">
              In · {room} {roomCount > 0 && `· ${roomCount} recorded`}
            </div>
            <h1 className="font-serif-display text-4xl text-[#0A192F] leading-tight">
              {roomCount === 0 ? "Pick up the first object." : "Next object."}
            </h1>
            <p className="text-sm text-[#5C5F58] mt-3">
              Describe it out loud, or photograph its label. We&apos;ll stitch the record together.
            </p>
          </header>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-0 border border-[#E0DDD6] bg-white">
            <button
              onClick={() => setPhase("voice")}
              data-testid="capture-voice"
              className="p-8 border-r border-b sm:border-b-0 border-[#E0DDD6] text-left hover:bg-[#F9F8F6] transition-colors"
            >
              <div className="w-14 h-14 bg-[#1A3622] flex items-center justify-center mb-4">
                <Microphone size={28} weight="regular" color="#F9F8F6" />
              </div>
              <h3 className="font-serif-display text-2xl text-[#0A192F]">Narrate</h3>
              <p className="text-xs text-[#5C5F58] mt-2">
                Speak freely. Name, brand, serial, condition, origin — in any order.
              </p>
            </button>
            <button
              onClick={() => setPhase("photo")}
              data-testid="capture-photo"
              className="p-8 text-left hover:bg-[#F9F8F6] transition-colors"
            >
              <div className="w-14 h-14 bg-[#0A192F] flex items-center justify-center mb-4">
                <Camera size={28} weight="regular" color="#F9F8F6" />
              </div>
              <h3 className="font-serif-display text-2xl text-[#0A192F]">Photograph</h3>
              <p className="text-xs text-[#5C5F58] mt-2">
                Shoot the data plate or label. We&apos;ll extract model + serial.
              </p>
            </button>
          </div>

          <div className="flex justify-between items-center text-xs pt-4">
            <button
              onClick={changeRoom}
              data-testid="change-room"
              className="text-[#5C5F58] hover:text-[#0A192F] inline-flex items-center gap-1.5"
            >
              <MapPin size={12} /> Change room
            </button>
            {totalCount > 0 && (
              <Link
                to="/assets"
                data-testid="finish-session"
                className="text-[#1A3622] underline underline-offset-4"
              >
                Finish · view ledger →
              </Link>
            )}
          </div>
        </section>
      )}

      {/* PHASE 3: Voice */}
      {phase === "voice" && (
        <VoicePhase
          onBack={() => setPhase("capture")}
          onParsed={(p) => { setDraft(p); setPhase("confirm"); }}
          processing={processing}
          setProcessing={setProcessing}
        />
      )}

      {/* PHASE 4: Photo */}
      {phase === "photo" && (
        <PhotoPhase
          onBack={() => setPhase("capture")}
          onParsed={(p) => { setDraft(p); setPhase("confirm"); }}
          processing={processing}
          setProcessing={setProcessing}
        />
      )}

      {/* PHASE 5: Confirm */}
      {phase === "confirm" && draft && (
        <ConfirmPhase
          draft={draft}
          room={room}
          onRetry={resetDraft}
          onSave={saveDraft}
        />
      )}
    </div>
  );
}

/* ---------------- Voice Phase ---------------- */
function VoicePhase({ onBack, onParsed, processing, setProcessing }) {
  const mediaRecorderRef = useRef(null);
  const chunksRef = useRef([]);
  const timerRef = useRef(null);
  const [recording, setRecording] = useState(false);
  const [blob, setBlob] = useState(null);
  const [elapsed, setElapsed] = useState(0);

  const start = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mr = new MediaRecorder(stream);
      chunksRef.current = [];
      mr.ondataavailable = (e) => { if (e.data.size > 0) chunksRef.current.push(e.data); };
      mr.onstop = () => {
        const b = new Blob(chunksRef.current, { type: "audio/webm" });
        setBlob(b);
        stream.getTracks().forEach((t) => t.stop());
      };
      mr.start();
      mediaRecorderRef.current = mr;
      setRecording(true);
      setElapsed(0);
      timerRef.current = setInterval(() => setElapsed((e) => e + 1), 1000);
    } catch {
      toast.error("Microphone access denied.");
    }
  };

  const stop = () => {
    if (mediaRecorderRef.current?.state === "recording") mediaRecorderRef.current.stop();
    setRecording(false);
    if (timerRef.current) clearInterval(timerRef.current);
  };

  const process = async () => {
    if (!blob) return;
    setProcessing(true);
    try {
      const fd = new FormData();
      fd.append("file", blob, "walkthrough.webm");
      const res = await api.post("/assets/parse-voice", fd, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 120000,
      });
      onParsed(res.data.asset || {});
    } catch {
      toast.error("Transcription failed.");
    } finally {
      setProcessing(false);
    }
  };

  const uploadFile = (e) => {
    const f = e.target.files?.[0];
    if (f) setBlob(f);
  };

  const fmtTime = (s) => `${String(Math.floor(s / 60)).padStart(2, "0")}:${String(s % 60).padStart(2, "0")}`;

  return (
    <section data-testid="phase-voice" className="space-y-4">
      <button onClick={onBack} className="text-sm text-[#5C5F58] inline-flex items-center gap-2">
        <CaretLeft size={14} /> Back
      </button>
      <div className="bg-[#F9F8F6] border border-[#E0DDD6] p-8 flex flex-col items-center gap-6">
        <div className={`w-28 h-28 rounded-full border-2 flex items-center justify-center ${recording ? "border-[#6B1718] bg-[#6B1718]/5 animate-pulse" : "border-[#1A3622] bg-white"}`}>
          <Microphone size={44} weight={recording ? "fill" : "regular"} color={recording ? "#6B1718" : "#1A3622"} />
        </div>
        <div className="font-mono-ledger text-2xl tabular-nums text-[#0A192F]">{fmtTime(elapsed)}</div>
        <div className="flex gap-2">
          {!recording && !blob && (
            <button onClick={start} data-testid="walkthrough-rec-start" className="bg-[#1A3622] text-[#F9F8F6] px-6 py-3 text-sm border border-[#0F2818]">
              Start Recording
            </button>
          )}
          {recording && (
            <button onClick={stop} data-testid="walkthrough-rec-stop" className="bg-[#6B1718] text-white px-6 py-3 text-sm border border-[#6B1718] inline-flex items-center gap-2">
              <Stop size={16} weight="fill" /> Stop
            </button>
          )}
          {blob && !recording && (
            <>
              <button onClick={process} disabled={processing} data-testid="walkthrough-rec-process" className="bg-[#1A3622] text-[#F9F8F6] px-6 py-3 text-sm border border-[#0F2818] disabled:opacity-60">
                {processing ? "Parsing…" : "Transcribe & parse"}
              </button>
              <button onClick={() => { setBlob(null); setElapsed(0); }} className="px-4 py-3 text-sm border border-[#E0DDD6] bg-white inline-flex items-center gap-2">
                <ArrowClockwise size={14} /> Retake
              </button>
            </>
          )}
        </div>
        <label className="text-xs text-[#5C5F58] inline-flex items-center gap-2 cursor-pointer hover:text-[#0A192F]">
          <UploadSimple size={12} /> or upload audio file
          <input type="file" accept="audio/*" onChange={uploadFile} className="hidden" />
        </label>
      </div>
    </section>
  );
}

/* ---------------- Photo Phase ---------------- */
function PhotoPhase({ onBack, onParsed, processing, setProcessing }) {
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);

  const pick = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setFile(f);
    setPreview(URL.createObjectURL(f));
  };

  const process = async () => {
    if (!file) return;
    setProcessing(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await api.post("/assets/parse-image", fd, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 120000,
      });
      const extracted = res.data.extracted || {};
      const photoPath = res.data.file?.storage_path ? [res.data.file.storage_path] : [];
      onParsed({ ...extracted, photo_paths: photoPath });
    } catch {
      toast.error("Extraction failed.");
    } finally {
      setProcessing(false);
    }
  };

  return (
    <section data-testid="phase-photo" className="space-y-4">
      <button onClick={onBack} className="text-sm text-[#5C5F58] inline-flex items-center gap-2">
        <CaretLeft size={14} /> Back
      </button>
      <div className="bg-[#F9F8F6] border border-[#E0DDD6] aspect-[4/3] flex items-center justify-center overflow-hidden">
        {preview ? (
          <img src={preview} alt="Preview" className="max-w-full max-h-full object-contain" />
        ) : (
          <div className="text-center p-6">
            <Camera size={48} color="#5C5F58" className="mx-auto mb-3" />
            <p className="text-sm text-[#5C5F58]">Tap below to capture or pick</p>
          </div>
        )}
      </div>
      <div className="flex flex-col gap-2">
        <label className="inline-flex items-center justify-center gap-2 px-5 py-3 bg-[#1A3622] text-[#F9F8F6] text-sm cursor-pointer hover:bg-[#0F2818] border border-[#0F2818]">
          <Camera size={16} /> {file ? "Retake / choose another" : "Open camera / gallery"}
          <input
            type="file"
            accept="image/jpeg,image/png,image/webp"
            capture="environment"
            onChange={pick}
            className="hidden"
            data-testid="walkthrough-photo-input"
          />
        </label>
        {file && (
          <button
            onClick={process}
            disabled={processing}
            data-testid="walkthrough-photo-process"
            className="px-5 py-3 text-sm bg-white border border-[#1A3622] text-[#1A3622] disabled:opacity-60"
          >
            {processing ? "Reading label…" : "Extract details"}
          </button>
        )}
      </div>
    </section>
  );
}

/* ---------------- Confirm Phase ---------------- */
function ConfirmPhase({ draft, room, onRetry, onSave }) {
  const [form, setForm] = useState({
    name: draft.name || "",
    category: draft.category || "other",
    brand: draft.brand || "",
    model: draft.model || "",
    serial: draft.serial || "",
    condition: draft.condition || "",
    fmv: draft.fmv ?? "",
    rcv: draft.rcv ?? "",
    notes: draft.notes || "",
  });
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm({ ...form, [k]: v });

  const commit = async () => {
    if (!form.name.trim()) { toast.error("Name is required."); return; }
    setSaving(true);
    const payload = {
      ...form,
      fmv: form.fmv === "" ? null : parseFloat(form.fmv),
      rcv: form.rcv === "" ? null : parseFloat(form.rcv),
    };
    await onSave(payload);
    setSaving(false);
  };

  return (
    <section data-testid="phase-confirm" className="space-y-4">
      <button onClick={onRetry} className="text-sm text-[#5C5F58] inline-flex items-center gap-2">
        <CaretLeft size={14} /> Retake
      </button>
      <div className="bg-white border border-[#E0DDD6] p-6 space-y-4">
        <div>
          <div className="font-mono-ledger text-[10px] uppercase tracking-[0.25em] text-[#5C5F58] mb-1">
            Draft · {room}
          </div>
          <h2 className="font-serif-display text-2xl text-[#0A192F]">Confirm & commit</h2>
        </div>

        <Inp label="Name *" value={form.name} onChange={(v) => set("name", v)} testid="confirm-name" />
        <div className="grid grid-cols-2 gap-3">
          <Sel label="Class" value={form.category} onChange={(v) => set("category", v)} />
          <Inp label="Brand" value={form.brand} onChange={(v) => set("brand", v)} />
          <Inp label="Model" value={form.model} onChange={(v) => set("model", v)} />
          <Inp label="Serial" value={form.serial} onChange={(v) => set("serial", v)} mono />
          <Inp label="FMV (USD)" type="number" value={form.fmv} onChange={(v) => set("fmv", v)} testid="confirm-fmv" />
          <Inp label="RCV (USD)" type="number" value={form.rcv} onChange={(v) => set("rcv", v)} />
        </div>
        <Ta label="Notes" value={form.notes} onChange={(v) => set("notes", v)} />

        <div className="flex gap-2 pt-3 border-t border-[#E0DDD6]">
          <button onClick={onRetry} className="flex-1 px-4 py-3 text-sm border border-[#E0DDD6] bg-white">
            Discard
          </button>
          <button
            onClick={commit}
            disabled={saving}
            data-testid="confirm-save"
            className="flex-1 inline-flex items-center justify-center gap-2 bg-[#1A3622] text-[#F9F8F6] px-4 py-3 text-sm border border-[#0F2818] disabled:opacity-60"
          >
            <Check size={14} weight="bold" /> {saving ? "Saving…" : "Save & next"}
          </button>
        </div>
      </div>
    </section>
  );
}

function Inp({ label, value, onChange, type = "text", testid, mono }) {
  return (
    <label className="block">
      <span className="block font-mono-ledger text-[10px] uppercase tracking-[0.25em] text-[#5C5F58] mb-1.5">{label}</span>
      <input
        data-testid={testid}
        type={type}
        value={value ?? ""}
        onChange={(e) => onChange(e.target.value)}
        className={`w-full bg-white border border-[#E0DDD6] px-3 py-2 text-sm focus:outline-none focus:border-[#1A3622] ${mono ? "font-mono-ledger" : ""}`}
      />
    </label>
  );
}

function Ta({ label, value, onChange }) {
  return (
    <label className="block">
      <span className="block font-mono-ledger text-[10px] uppercase tracking-[0.25em] text-[#5C5F58] mb-1.5">{label}</span>
      <textarea
        rows={2}
        value={value ?? ""}
        onChange={(e) => onChange(e.target.value)}
        className="w-full bg-white border border-[#E0DDD6] px-3 py-2 text-sm focus:outline-none focus:border-[#1A3622]"
      />
    </label>
  );
}

function Sel({ label, value, onChange }) {
  return (
    <label className="block">
      <span className="block font-mono-ledger text-[10px] uppercase tracking-[0.25em] text-[#5C5F58] mb-1.5">{label}</span>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-full bg-white border border-[#E0DDD6] px-3 py-2 text-sm focus:outline-none focus:border-[#1A3622]"
      >
        {CATEGORIES.map((o) => (<option key={o.value} value={o.value}>{o.label}</option>))}
      </select>
    </label>
  );
}
